function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
import { rune } from "./rune";
import { VirtualView } from "./VirtualView";
import { each, pipe, zip } from "@fxts/core";
import { $ } from "./$Element";
import { ViewMounted, ViewRendered, ViewUnmounted } from "./ViewEvent";
export class View extends VirtualView {
    render() {
        return this._render(this.toHtml());
    }
    _render(html) {
        return this._setElement($.fromHtml(html).element()).hydrate().element();
    }
    hydrateFromSSR(element) {
        return this._setElement(element)._makeHtml().hydrate();
    }
    hydrate() {
        this.hydrateSubViews()._onRender();
        if (document.body.contains(this.element())) {
            this._onMount();
        }
        return this;
    }
    hydrateSubViews() {
        if (this.subViewsFromTemplate.length > 0) {
            pipe(zip(this._subViewElements(), this.subViewsFromTemplate), each((param)=>{
                let [element, view] = param;
                view._setElement(element).hydrate();
            }));
        }
        return this;
    }
    _onRender() {
        this.addEventListener(ViewMounted, this._onMount);
        this.addEventListener(ViewUnmounted, this._onUnmount);
        this._reservedEnables = this.constructor._ReservedEnables.map((ReservedEnable)=>new ReservedEnable(this));
        rune.set(this.element(), this, View);
        super._onRender();
        this.dispatchEvent(ViewRendered, {
            detail: this
        });
        return this;
    }
    _redrawAttributes() {
        const { startTag, startTagName } = this._matchStartTag(this._currentHtml);
        const element2 = $.fromHtml("".concat(startTag, "</").concat(startTagName, ">")).element();
        const element = this.element();
        let length = element.attributes.length;
        while(--length){
            const { name } = element.attributes[length];
            if (!element2.attributes[name]) {
                element.removeAttribute(name);
            }
        }
        for (const { name, value } of element2.attributes){
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const attr = element.attributes[name];
            if (!attr || attr.value !== value) {
                element.setAttribute(name, value);
            }
        }
        return this;
    }
    _setInnerHtmlFromCurrentInnerHtml() {
        $(this.element()).setInnerHtml(this._currentInnerHtml());
        return this;
    }
    redraw() {
        return this._makeHtml().safely(()=>this._redrawAttributes()._setInnerHtmlFromCurrentInnerHtml().hydrateSubViews());
    }
    _subViewSelector(subViewName) {
        return '[data-rune-parent="'.concat(this, '"]').concat(subViewName && subViewName !== "View" ? ".".concat(subViewName) : "");
    }
    _firstDepthSubViews(parentEl, subViewName) {
        return $(parentEl).findAll(this._subViewSelector(subViewName)).map(($el)=>$el.element()).filter((el)=>{
            var _el_parentElement;
            return this.element() === ((_el_parentElement = el.parentElement) === null || _el_parentElement === void 0 ? void 0 : _el_parentElement.closest('[data-rune="'.concat(this, '"]')));
        });
    }
    _subViewElements(subViewName) {
        return this._firstDepthSubViews(this.element(), subViewName);
    }
    _subViewElementsIn(selector, subViewName) {
        return $(this.element()).findAll(selector).map(($el)=>$el.element()).flatMap((el)=>this._firstDepthSubViews(el, subViewName));
    }
    _subViews(elements, SubView) {
        return elements.map((element)=>rune.getView(element, SubView));
    }
    subViews(SubView, selector) {
        return selector !== undefined ? this.subViewsIn(selector, SubView) : this._subViews(this._subViewElements(SubView.name), SubView);
    }
    subViewsIn(selector, SubView) {
        return this._subViews(this._subViewElementsIn(selector, SubView.name), SubView);
    }
    _firstDepthSubView(parentEl, subViewName) {
        var _$_find;
        var _$_find_element;
        return parentEl ? (_$_find_element = (_$_find = $(parentEl).find(this._subViewSelector(subViewName))) === null || _$_find === void 0 ? void 0 : _$_find.element()) !== null && _$_find_element !== void 0 ? _$_find_element : null : null;
    }
    _subViewElement(subViewName) {
        return this._firstDepthSubView(this.element(), subViewName);
    }
    _subViewElementIn(selector, subViewName) {
        var _$_find;
        return this._firstDepthSubView((_$_find = $(this.element()).find(selector)) === null || _$_find === void 0 ? void 0 : _$_find.element(), subViewName);
    }
    _subView(element, SubView) {
        var _rune_getView;
        return element ? (_rune_getView = rune.getView(element, SubView)) !== null && _rune_getView !== void 0 ? _rune_getView : null : null;
    }
    subView(SubView, selector) {
        return selector !== undefined ? this.subViewIn(selector, SubView) : this._subView(this._subViewElement(SubView.name), SubView);
    }
    subViewIn(selector, SubView) {
        return this._subView(this._subViewElementIn(selector, SubView.name), SubView);
    }
    redrawOnlySubViews() {
        this.subViews(View).filter((view)=>!view.ignoreRefreshOnlySubViewFromParent).forEach((view)=>view.redraw());
        return this;
    }
    static createAndHydrate(element) {
        const dataEl = $(element).next("script.__RUNE_DATA__.".concat(this.name, '[data-rune-base-name="View"]'));
        if (dataEl === null) {
            throw new Error("No __RUNE_DATA__ script found");
        } else {
            var _dataEl_getTextContent;
            const hydration_data = JSON.parse((_dataEl_getTextContent = dataEl.getTextContent()) !== null && _dataEl_getTextContent !== void 0 ? _dataEl_getTextContent : "{}");
            dataEl.remove();
            return new this(hydration_data.data, ...hydration_data.args).hydrateFromSSR(element);
        }
    }
    constructor(...args){
        super(...args);
        _define_property(this, "_base_name", "View");
        _define_property(this, "subViewsFromTemplate", []);
        _define_property(this, "ignoreRefreshOnlySubViewFromParent", false);
        /* eslint-disable @typescript-eslint/no-explicit-any */ _define_property(this, "_reservedEnables", []);
    }
}
_define_property(View, "_ReservedEnables", []);
 /* eslint-enable @typescript-eslint/no-explicit-any */ 